import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import {
    VAULT77_HEALTH_DEFAULT_COLOR,
    VAULT77_HEALTH_ISSUE_COLORS,
    VAULT77_HEALTH_ISSUE_LABELS,
    type Vault77HealthIssue,
} from "../types";
import "./HealthRowWarning.css";

export interface HealthRowWarningProps {
    employeeName?: string;
    /** All issues for the employee. Empty = in compliance (renders an invisible, fixed-width spacer). */
    issues: Vault77HealthIssue[];
}

function issueLabel(code: string): string {
    return VAULT77_HEALTH_ISSUE_LABELS[code] || code;
}

function issueColor(code: string): string {
    return VAULT77_HEALTH_ISSUE_COLORS[code] || VAULT77_HEALTH_DEFAULT_COLOR;
}

function detail(issue: Vault77HealthIssue): string {
    let text = issue.Message;
    // Show the FieldResponse (external) review id — the id HR sees everywhere else. Only fall back to
    // the internal Vault77 id when there's no external id (a broken/dangling link or an unlinked review).
    if (issue.ExternalReviewId) text += ` (review #${issue.ExternalReviewId})`;
    else if (issue.EmployeeReviewId) text += ` (review #${issue.EmployeeReviewId} — internal)`;
    if (issue.PayRateHistoryId) text += ` (pay rate #${issue.PayRateHistoryId})`;
    if (issue.EffectiveDate) text += ` — ${issue.EffectiveDate.slice(0, 10)}`;
    return text;
}

/**
 * Health indicator for an employee table row, next to the name:
 *  - no issues  -> an invisible, fixed-width spacer (NO green check) so the name text stays aligned
 *    with rows that do show an icon — only problems are surfaced;
 *  - has issues -> a single amber warning icon with a popover listing the issues, color-coded by
 *    type (a colored dot + colored type label per group, matching the header gauge colors).
 * The spacer and the icon share the same fixed-width slot, so toggling between them never shifts the
 * name. Clicks are swallowed so reading the popover doesn't also open the row.
 */
const HealthRowWarning: React.FC<HealthRowWarningProps> = ({ employeeName, issues }) => {
    if (!issues || issues.length === 0) {
        return <span className="Vault77HealthRowWarning__slot" aria-hidden="true" />;
    }

    // Group by issue type (first-seen order) so the popover lists one color-coded entry per type.
    const groups = new Map<string, Vault77HealthIssue[]>();
    for (const issue of issues) {
        const list = groups.get(issue.Code);
        if (list) list.push(issue);
        else groups.set(issue.Code, [issue]);
    }

    const popover = (
        <Popover id="vault77-health-row" className="Vault77HealthRowPopover">
            <Popover.Title as="h3">
                {issues.length} issue{issues.length === 1 ? "" : "s"}
                {employeeName ? ` · ${employeeName}` : ""}
            </Popover.Title>
            <Popover.Content>
                <ul className="Vault77HealthRowPopover__list">
                    {Array.from(groups.entries()).map(([code, list]) => {
                        const color = issueColor(code);
                        return (
                            <li key={code} className="Vault77HealthRowPopover__group">
                                <span className="Vault77HealthRowPopover__label" style={{ color }}>
                                    <span className="Vault77HealthRowPopover__dot" style={{ backgroundColor: color }} />
                                    {issueLabel(code)}
                                    {list.length > 1 ? ` (${list.length})` : ""}
                                </span>
                                <ul className="Vault77HealthRowPopover__sub">
                                    {list.map((issue) => (
                                        <li
                                            key={`${code}-${issue.EmployeeReviewId ?? issue.PayRateHistoryId ?? issue.Message}`}
                                            className="Vault77HealthRowPopover__msg"
                                        >
                                            {detail(issue)}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        );
                    })}
                </ul>
            </Popover.Content>
        </Popover>
    );

    return (
        <OverlayTrigger trigger={["hover", "focus"]} placement="right" overlay={popover}>
            <span
                className="Vault77HealthRowWarning__slot"
                role="img"
                aria-label={`${issues.length} data ${issues.length === 1 ? "issue" : "issues"}`}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <span className="material-icons Vault77HealthRowWarning__icon">warning</span>
            </span>
        </OverlayTrigger>
    );
};

export default HealthRowWarning;
